import type { FC, ReactNode } from 'react'
import type { LoginRole, ModuleId, PlanId, PublicStudent } from '@/types'

import { useCallback, useState } from 'react'
import { useRouter } from 'next/router'
import { useViewer } from '@/hooks/use-viewer'
import { Layout } from '@/components/generals/layout'
import { Navbar } from '@/components/landing/navbar'
import { Hero, HeroBackdrop } from '@/components/landing/hero'
import { ProductShowcase } from '@/components/landing/product-showcase'
import { TrustBanner } from '@/components/landing/trust-banner'
import { Pricing } from '@/components/landing/pricing'
import { Bento } from '@/components/landing/bento'
import { FeatureAccordion } from '@/components/landing/feature-accordion'
import { InquirySection } from '@/components/landing/inquiry-section'
import { Footer } from '@/components/landing/footer'
import { CheckoutModal } from '@/components/landing/checkout-modal'

import { MODULES } from '@/constants/campus'

// The public entry screen. Visitors explore here, register and pay from the
// checkout, or jump to sign in; signed-in users get a shortcut to their portal.
export const LandingPage: FC = (): ReactNode => {
    const router = useRouter()
    const { viewer, setViewer } = useViewer()
    const [activeModule, setActiveModule] = useState<ModuleId>('student-hub')
    const [autoPlay, setAutoPlay] = useState(true)
    const [checkout, setCheckout] = useState<{ open: boolean, planId: PlanId }>({ open: false, planId: 'complete' })

    const onOpenLogin = useCallback((role: LoginRole, signUp = false) => {
        router.push({ pathname: '/login', query: signUp ? { role, mode: 'signup' } : { role } })
    }, [router])

    const selectModule = useCallback((id: ModuleId) => {
        setActiveModule(id)
        setAutoPlay(false)
    }, [])

    const jumpToModule = useCallback((id: ModuleId) => {
        selectModule(id)

        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        document.getElementById('modules')?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
    }, [selectModule])

    const autoAdvance = useCallback(() => {
        setActiveModule(current => MODULES[(MODULES.findIndex(module => module.id === current) + 1) % MODULES.length].id)
    }, [])

    const choosePlan = useCallback((planId: PlanId) => {
        if (viewer?.kind === 'student' && ['Pending', 'Active'].includes(viewer.student.accessStatus)) {
            router.push('/portal')
            return
        }

        setCheckout({ open: true, planId })
    }, [viewer, router])

    const closeCheckout = useCallback(() => setCheckout(current => ({ ...current, open: false })), [])
    const changePlan = useCallback((planId: PlanId) => setCheckout(current => ({ ...current, planId })), [])
    const onRegistered = useCallback((student: PublicStudent) => setViewer({ kind: 'student', student }), [setViewer])

    return (
        <Layout
            header={<Navbar viewer={viewer} onSelectModule={jumpToModule} onOpenLogin={onOpenLogin} />}
            footer={<Footer onSelectModule={jumpToModule} onOpenLogin={onOpenLogin} />}
        >
            <div className='relative isolate overflow-hidden bg-midnight'>
                <HeroBackdrop />
                <Hero viewer={viewer} onOpenLogin={onOpenLogin} />
                <ProductShowcase active={activeModule} autoPlay={autoPlay} onSelect={selectModule} onAutoAdvance={autoAdvance} />
            </div>

            <TrustBanner />
            <Pricing viewer={viewer} onChoosePlan={choosePlan} />
            <Bento />
            <FeatureAccordion />
            <InquirySection onOpenLogin={onOpenLogin} />

            <CheckoutModal
                open={checkout.open}
                planId={checkout.planId}
                viewer={viewer}
                onClose={closeCheckout}
                onPlanChange={changePlan}
                onRegistered={onRegistered}
            />
        </Layout>
    )
}
